import { z } from 'zod';

// Transport layer for embedding this app in a native iOS/Android WebView.
// Protocol (see docs/native-webview-auth.md for the native-side contract):
//
//   web -> native  { type: 'request-session', requestId }
//   native -> web  { type: 'session', requestId, accessToken, user }        (correlated reply)
//   native -> web  { type: 'session', requestId: null, accessToken, user }  (unsolicited push, e.g. background refresh)
//   native -> web  { type: 'session-ended' }                                (unsolicited logout)
//
// Every request is correlated by `requestId` so a slow/out-of-order native
// reply can never be mistaken for the answer to a different call — the bug
// the previous WebView bridge had (fire-and-forget request, no correlation).

export interface NativeUserClaims {
  userId: string;
  email?: string;
  name?: string;
  picture?: string;
  clientId?: string;
  homeTenant?: string;
  roleId?: string;
  targetTenant?: string;
  [key: string]: unknown;
}

export interface NativeSessionMessage {
  type: 'session';
  requestId: string | null;
  accessToken: string;
  user: NativeUserClaims;
}

const SESSION_ENDED_TYPE = 'session-ended';

interface NativeSessionEndedMessage {
  type: typeof SESSION_ENDED_TYPE;
}

type NativeMessage = NativeSessionMessage | NativeSessionEndedMessage;

const REQUEST_TIMEOUT_MS = 5000;

// The optional globals a host native app may inject into the page. Declared
// once here and read only through `nativeWindow` below so every access is a
// checked property read on a named, fully-typed value — never an inline cast.
interface NativeWebViewWindow {
  __EncoreNativeWebView?: boolean;
  ReactNativeWebView?: { postMessage?(message: string): void };
  webkit?: {
    messageHandlers?: {
      EncoreNative?: { postMessage?(message: unknown): void };
    };
  };
  EncoreNative?: { postMessage?(message: string): void };
}

// `window` cast through the native-bridge globals a host app may inject.
// Single named cast, one-line reason: these globals are host-injected and
// have no other declaration site to check against.
const nativeWindow = window as unknown as NativeWebViewWindow;

export function isEmbeddedWebView(): boolean {
  // Dev-only: a production visitor must never be able to flip the app into
  // native mode via a URL param -- there's no bridge in a browser, so
  // requestSessionFromNative always times out and the user is stuck on a
  // permanent loading state with no way back to the real LoginPage.
  if (import.meta.env.DEV) {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('webview') === '1') {
        return true; // browser-testing override, dev builds only
      }
    } catch {
      // ignore URL parse errors
    }
  }
  if (nativeWindow.__EncoreNativeWebView) {
    return true; // native iOS/Android marker, injected before page scripts run
  }
  if (nativeWindow.ReactNativeWebView) {
    return true; // React Native WebView
  }
  return false;
}

// Fallback counter for environments without crypto.randomUUID (older WebView
// JS engines). A monotonic counter is sufficient — requestSessionFromNative
// is single-flighted, so at most one request is ever outstanding from this
// module — and avoids Math.random(), which static analysis (correctly, in
// general) flags as an unsuitable source for anything identifier-like.
let fallbackRequestIdCounter = 0;

function generateRequestId(): string {
  if (typeof crypto?.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  fallbackRequestIdCounter += 1;
  return `${Date.now()}-${fallbackRequestIdCounter}`;
}

// Parsed once at this untrusted boundary (postMessage from native) via a
// schema, rather than hand-rolled Record<string, unknown> guards — see
// NativeUserClaims/NativeSessionMessage above for the types this produces.
const NativeUserClaimsSchema = z
  .object({ userId: z.string().min(1) })
  .catchall(z.unknown());

const IncomingMessageSchema = z.union([
  z.object({
    type: z.literal('session'),
    requestId: z.string().nullish(),
    accessToken: z.string().min(1),
    user: NativeUserClaimsSchema,
  }),
  z.object({ type: z.literal(SESSION_ENDED_TYPE) }),
]);

function normalizeMessage(raw: unknown): NativeMessage | null {
  let candidate: unknown = raw;
  if (typeof raw === 'string') {
    try {
      candidate = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const result = IncomingMessageSchema.safeParse(candidate);
  if (!result.success) {
    return null;
  }
  if (result.data.type === SESSION_ENDED_TYPE) {
    return { type: SESSION_ENDED_TYPE };
  }
  return {
    type: 'session',
    requestId: result.data.requestId ?? null,
    accessToken: result.data.accessToken,
    user: result.data.user,
  };
}

/** Posts a message to whichever native bridge is present. Returns false if none is. */
function postToNative(message: unknown): boolean {
  const payload = JSON.stringify(message);
  if (nativeWindow.ReactNativeWebView?.postMessage) {
    nativeWindow.ReactNativeWebView.postMessage(payload);
    return true;
  }
  if (nativeWindow.webkit?.messageHandlers?.EncoreNative?.postMessage) {
    nativeWindow.webkit.messageHandlers.EncoreNative.postMessage(message);
    return true;
  }
  if (nativeWindow.EncoreNative?.postMessage) {
    nativeWindow.EncoreNative.postMessage(payload);
    return true;
  }
  return false;
}

interface PendingRequest {
  resolve: (message: NativeSessionMessage) => void;
  timer: ReturnType<typeof setTimeout>;
}

const pendingRequests = new Map<string, PendingRequest>();
const sessionListeners = new Set<(message: NativeSessionMessage) => void>();
const sessionEndedListeners = new Set<() => void>();
let listenersInstalled = false;

function processIncoming(data: unknown) {
  const message = normalizeMessage(data);
  if (!message) {
    return;
  }
  if (message.type === SESSION_ENDED_TYPE) {
    sessionEndedListeners.forEach(listener => listener());
    return;
  }
  if (message.requestId) {
    const pending = pendingRequests.get(message.requestId);
    if (pending) {
      clearTimeout(pending.timer);
      pendingRequests.delete(message.requestId);
      pending.resolve(message);
    }
    // A correlated reply belongs only to its requester, which applies its
    // own generation/staleness check (see nativeIdentity.tsx). A late
    // reply whose request already timed out (no `pending` entry left) is
    // just as unsafe to replay to every session listener as an unsolicited
    // push would be -- broadcasting it here previously let a revoked
    // session's stale reply resurrect that session via the listener path,
    // bypassing the requester's own staleness guard entirely.
    return;
  }
  sessionListeners.forEach(listener => listener(message));
}

function handleWindowMessage(event: MessageEvent | CustomEvent) {
  if (!(event instanceof MessageEvent)) {
    return;
  }
  // Only accept same-origin, self-posted messages (native's
  // evaluateJavaScript/postMessage runs as the top-level document, so both
  // origin and source match this window). Rejects cross-origin senders and
  // messages relayed from a nested iframe pretending to be the native bridge.
  if (event.origin !== window.location.origin || event.source !== window) {
    return;
  }
  processIncoming(event.data);
}

// Some native WebView SDKs (older Android React Native WebView versions)
// dispatch the reply as a MessageEvent on `document` rather than `window`.
// That event is constructed in-page by the injected bridge script -- not a
// real cross-context postMessage -- so it always carries an empty origin and
// a null source. The window-path check above would reject every such event,
// making this listener permanently dead code, so this path skips it:
// dispatching a MessageEvent on `document` already requires script running
// inside this page, which is no more privileged than any other code already
// executing here, so there is no cross-origin/cross-frame boundary left to
// enforce for this target.
function handleDocumentMessage(event: Event) {
  if (!(event instanceof MessageEvent)) {
    return;
  }
  processIncoming(event.data);
}

function ensureListenersInstalled() {
  if (listenersInstalled) {
    return;
  }
  listenersInstalled = true;
  window.addEventListener('message', handleWindowMessage);
  // See handleDocumentMessage for why this path skips origin/source
  // validation.
  document.addEventListener('message', handleDocumentMessage);
}

/** Subscribes to unsolicited native pushes (background refresh / logout). */
export function subscribeToNativeSession(
  onSession: (message: NativeSessionMessage) => void,
  onSessionEnded: () => void
): () => void {
  ensureListenersInstalled();
  sessionListeners.add(onSession);
  sessionEndedListeners.add(onSessionEnded);
  return () => {
    sessionListeners.delete(onSession);
    sessionEndedListeners.delete(onSessionEnded);
  };
}

let inFlightRequest: Promise<NativeSessionMessage> | null = null;

/**
 * Requests the current session (token + user claims) from native. Single-
 * flighted: concurrent callers share one native round-trip rather than each
 * triggering their own. Rejects on timeout or if no native bridge is present
 * — callers decide whether to fall back to a previously cached session.
 */
export function requestSessionFromNative(): Promise<NativeSessionMessage> {
  if (inFlightRequest) {
    return inFlightRequest;
  }
  ensureListenersInstalled();
  const requestId = generateRequestId();
  // `new Promise` executor, not `Promise.withResolvers`: this module can run
  // inside older native WebView JS engines that predate that 2024 API.
  inFlightRequest = new Promise<NativeSessionMessage>((resolve, reject) => {
    const timer = setTimeout(() => {
      pendingRequests.delete(requestId);
      reject(
        new Error(
          `Timed out after ${REQUEST_TIMEOUT_MS}ms waiting for native session response`
        )
      );
    }, REQUEST_TIMEOUT_MS);
    pendingRequests.set(requestId, { resolve, timer });
    const posted = postToNative({ type: 'request-session', requestId });
    if (!posted) {
      clearTimeout(timer);
      pendingRequests.delete(requestId);
      reject(new Error('No native WebView bridge is available'));
    }
  }).finally(() => {
    inFlightRequest = null;
  });
  return inFlightRequest;
}

/** Tells native the web app is logging out; native owns the resulting UX. */
export function notifyNativeLogout(): void {
  postToNative({ type: 'logout-request' });
}
